import Phaser from 'phaser';
import type { Battlefield, CombatPosition } from '@hexcrawl/shared-utils';
import type { AnimatedSprite } from '@/utils/AnimatedSprite';
import type { CombatCoordinates } from './CombatCoordinates';

export type CombatMoveTweenOptions = {
  ignoreEntityId?: string;
  ease?: string;
  msPerTile?: number;
};

type WorldPoint = { x: number; y: number };

function manhattanWorldDistance(a: WorldPoint, b: WorldPoint): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

export function createCombatMoveTween(
  scene: Phaser.Scene,
  sprite: AnimatedSprite,
  coords: CombatCoordinates,
  battlefield: Battlefield,
  from: CombatPosition,
  to: CombatPosition,
  options: CombatMoveTweenOptions = {}
): Phaser.Tweens.Tween {
  const ease = options.ease ?? 'Sine.easeOut';
  const msPerTile = options.msPerTile ?? 1000;

  const path = battlefield.findPath(from, to, { ignoreEntityId: options.ignoreEntityId });

  const goalWorld = coords.tileToWorld(to.x + 0.5, to.y + 0.5);

  if (!path || path.length < 2) {
    sprite.setFacing(coords.getFacingBetweenPositions(from, to));
    return scene.tweens.add({
      targets: sprite,
      x: goalWorld.x,
      y: goalWorld.y,
      duration: msPerTile,
      ease,
      onUpdate: () => sprite.updateOverlayPositions(),
    });
  }

  const tiles = path;
  const worldPoints: WorldPoint[] = tiles.map((p) => coords.tileToWorld(p.x + 0.5, p.y + 0.5));
  worldPoints[0] = { x: sprite.x, y: sprite.y };

  const segmentLengths: number[] = [];
  const cumulative: number[] = [0];
  let totalDistance = 0;
  for (let i = 0; i < worldPoints.length - 1; i++) {
    const segLen = manhattanWorldDistance(worldPoints[i], worldPoints[i + 1]);
    segmentLengths.push(segLen);
    totalDistance += segLen;
    cumulative.push(totalDistance);
  }

  if (totalDistance <= 0) {
    sprite.setFacing(coords.getFacingBetweenPositions(from, to));
    sprite.x = goalWorld.x;
    sprite.y = goalWorld.y;
    return scene.tweens.addCounter({ from: 0, to: 1, duration: 0 });
  }

  let currentSegment = 0;
  const proxy = { d: 0 };

  // Initial facing for responsiveness.
  sprite.setFacing(coords.getFacingBetweenPositions(tiles[0], tiles[1]));

  return scene.tweens.add({
    targets: proxy,
    d: totalDistance,
    duration: (tiles.length - 1) * msPerTile,
    ease,
    onUpdate: () => {
      const dist = proxy.d;
      while (currentSegment < segmentLengths.length - 1 && dist >= cumulative[currentSegment + 1]) {
        currentSegment++;
        sprite.setFacing(coords.getFacingBetweenPositions(tiles[currentSegment], tiles[currentSegment + 1]));
      }

      const segLen = segmentLengths[currentSegment] ?? 0;
      const segStart = worldPoints[currentSegment];
      const segEnd = worldPoints[currentSegment + 1];

      if (!segStart || !segEnd) return;

      const segDist = dist - (cumulative[currentSegment] ?? 0);
      const t = segLen > 0 ? Phaser.Math.Clamp(segDist / segLen, 0, 1) : 1;
      sprite.x = segStart.x + (segEnd.x - segStart.x) * t;
      sprite.y = segStart.y + (segEnd.y - segStart.y) * t;
      sprite.updateOverlayPositions();
    },
    onComplete: () => {
      sprite.x = goalWorld.x;
      sprite.y = goalWorld.y;
      sprite.updateOverlayPositions();
    },
  });
}
